import { z } from 'zod';
import { SalesDescriptionSuggestionSchema, DescriptionLangSchema } from './salesDescription.js';
import { SizingActionSchema, type SizingInput } from './sizing.js';

/**
 * Chat-assistent (rechterkolom): API-contract tussen frontend en backend.
 * De assistent leest producten, Unit4-gegevens, sjablonen en sizing-referentie via tools en kan
 * ook handelen: wijzigingen aan het geopende product gaan als concept ("editor-patch") in het
 * formulier (de gebruiker slaat zelf op); server-acties (indienen, vernieuwen, pushen, archiveren,
 * wijzigen van een niet-geopend product) vragen eerst bevestiging via een kaart in het paneel.
 * Berichten worden per gebruiker bewaard in `chat_conversations` / `chat_messages`; het antwoord
 * komt als SSE-stream (zie `ChatStreamEvent`).
 */

/** Wat de gebruiker op dat moment open heeft (voor de "huidige context" in de system prompt). */
export const ChatContextSchema = z.object({
  /** Routenaam (bv. product-detail, products, import) */
  route: z.string().trim().max(60).nullable().optional(),
  product_id: z.number().int().positive().nullable().optional(),
  product_name: z.string().trim().max(200).nullable().optional(),
  /** Actief tabblad op de productpagina (lines/tree/details/validation/conflicts/sync) */
  tab: z.string().trim().max(30).nullable().optional(),
  /** true = dit product is geladen in de editor (wijzigingen kunnen als concept in het formulier) */
  editor_open: z.boolean().optional(),
  /** true = de editor heeft niet-opgeslagen wijzigingen */
  editor_dirty: z.boolean().optional(),
});
export type ChatContext = z.infer<typeof ChatContextSchema>;

export const ChatSendBodySchema = z.object({
  message: z.string().trim().min(1, 'Bericht is leeg').max(8000, 'Bericht is te lang (max. 8000 tekens)'),
  context: ChatContextSchema.nullable().optional(),
});
export type ChatSendBody = z.infer<typeof ChatSendBodySchema>;

/**
 * Niveau van een tool: read = leest alleen; write = maakt/wijzigt; delete = verwijdert/archiveert.
 * Write/delete-tools vragen bevestiging vóór ze iets doen (tenzij het een concept in de editor is).
 */
export const ChatToolTierSchema = z.enum(['read', 'write', 'delete']);
export type ChatToolTier = z.infer<typeof ChatToolTierSchema>;

/** Antwoord van de gebruiker op een bevestigingskaart (`POST …/messages/confirm`). */
export const ChatConfirmBodySchema = z.object({
  tool_use_id: z.string().min(1).max(200),
  decision: z.enum(['confirm', 'cancel']),
});
export type ChatConfirmBody = z.infer<typeof ChatConfirmBodySchema>;

/** Bouwsteen van een assistent-antwoord, in volgorde van ontstaan. */
export const ChatBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('thinking'), text: z.string() }),
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({
    type: z.literal('tool_use'),
    id: z.string(),
    name: z.string(),
    input: z.unknown(),
    tier: ChatToolTierSchema.optional(),
  }),
  z.object({
    type: z.literal('tool_result'),
    tool_use_id: z.string(),
    name: z.string(),
    is_error: z.boolean(),
    output: z.string(),
  }),
]);
export type ChatBlock = z.infer<typeof ChatBlockSchema>;

export const ChatMessageRoleSchema = z.enum(['user', 'assistant']);
export type ChatMessageRole = z.infer<typeof ChatMessageRoleSchema>;

export const ChatMessageSchema = z.object({
  id: z.number().int(),
  conversation_id: z.number().int(),
  role: ChatMessageRoleSchema,
  content: z.string(),
  blocks: z.array(ChatBlockSchema).nullable(),
  model: z.string().nullable(),
  usage: z.record(z.unknown()).nullable(),
  created_at: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatConversationSchema = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  title: z.string().nullable(),
  message_count: z.number().int().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ChatConversation = z.infer<typeof ChatConversationSchema>;

export const ChatTitleBodySchema = z.object({
  title: z.string().trim().min(1, 'Titel is verplicht').max(120),
});
export type ChatTitleBody = z.infer<typeof ChatTitleBodySchema>;

// ---------------------------------------------------------------------------
// Editor-patch: wijzigingen die de assistent als concept in het geopende product zet
// ---------------------------------------------------------------------------

/** Productvelden die de assistent mag voorstellen (null = leegmaken / sjabloon volgen). */
/**
 * Wijziging van de sizing-invoer vanuit de chat: alleen de opgegeven velden veranderen (weglaten =
 * houden, null = leegmaken). Bewust zonder defaults, anders zou een gedeeltelijke wijziging de rest
 * wissen. `null` voor het hele veld (in ChatProductFields) zet de sizing uit.
 */
export const ChatSizingPatchSchema = z.object({
  valve_torque: z.number().positive().nullable().optional(),
  supply_pressure: z.number().positive().nullable().optional(),
  safety_factor: z.number().min(1).nullable().optional(),
  sealing_factor: z.number().min(1).nullable().optional(),
  action: SizingActionSchema.nullable().optional(),
  valve_type: z.string().trim().max(60).nullable().optional(),
  spring_pack: z.string().trim().max(20).nullable().optional(),
});
export type ChatSizingPatch = z.infer<typeof ChatSizingPatchSchema>;

const EMPTY_SIZING: SizingInput = {
  valve_torque: null,
  supply_pressure: null,
  safety_factor: null,
  sealing_factor: null,
  action: null,
  valve_type: null,
  spring_pack: null,
};

/** Past een chat-wijziging toe op de huidige sizing-invoer (null-patch = sizing uit). */
export function mergeSizingPatch(
  current: SizingInput | null | undefined,
  patch: ChatSizingPatch | null,
): SizingInput | null {
  if (patch === null) return null;
  const out: SizingInput = { ...EMPTY_SIZING, ...(current ?? {}) };
  for (const [k, v] of Object.entries(patch) as Array<[keyof SizingInput, unknown]>)
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

export const ChatProductFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  search_name: z.string().trim().max(50).nullable().optional(),
  unit_code: z.string().trim().min(1).max(10).optional(),
  proposed_product_code: z.string().trim().max(50).nullable().optional(),
  long_description_nl: z.string().trim().max(4000).nullable().optional(),
  long_description_en: z.string().trim().max(4000).nullable().optional(),
  /** Alleen het instructiedeel (zonder de automatische productomschrijving erachter) */
  assembly_instruction: z.string().trim().max(12000).nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
  journal_entry_code: z.string().trim().max(10).nullable().optional(),
  warehouse_code: z.string().trim().max(10).nullable().optional(),
  location_code: z.string().trim().max(20).nullable().optional(),
  ean_code: z.string().trim().max(20).nullable().optional(),
  assembly_warehouse_code: z.string().trim().max(10).nullable().optional(),
  use_partial_relocation: z.enum(['J', 'N']).nullable().optional(),
  use_component_partial: z.enum(['J', 'N']).nullable().optional(),
  partial_relocation_minimum: z.number().min(0).nullable().optional(),
  apply_sub_assembly_end_product: z.enum(['J', 'N']).nullable().optional(),
  apply_sub_assembly_component: z.enum(['J', 'N']).nullable().optional(),
  /** Sizing-invoer: alleen de opgegeven velden; null = sizing uit (zie `mergeSizingPatch`) */
  sizing_input: ChatSizingPatchSchema.nullable().optional(),
});
export type ChatProductFields = z.infer<typeof ChatProductFieldsSchema>;

/** Secties van de verkoopomschrijving (alleen gevulde secties overschrijven, zoals "Voorstel met AI"). */
export const ChatSalesDescriptionPatchSchema = z.object({
  lang: DescriptionLangSchema,
  suggestion: SalesDescriptionSuggestionSchema,
});
export type ChatSalesDescriptionPatch = z.infer<typeof ChatSalesDescriptionPatchSchema>;

const lineFields = {
  description: z.string().trim().min(1).max(500),
  unit4_product_code: z.string().trim().max(50).nullable(),
  unit_code: z.string().trim().max(10).nullable(),
  qty_in_composition: z.number().positive(),
  composition_per: z.number().positive(),
  percentage_loss: z.number().min(0).max(99.9999),
  child_product_id: z.number().int().positive().nullable(),
  notes: z.string().max(5000).nullable(),
};

/** Regelmutaties: toevoegen, wijzigen (op regel-id) of verwijderen. */
export const ChatLineOpSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('add'),
    description: lineFields.description,
    unit4_product_code: lineFields.unit4_product_code.optional(),
    unit_code: lineFields.unit_code.optional(),
    qty_in_composition: lineFields.qty_in_composition.default(1),
    composition_per: lineFields.composition_per.default(1),
    percentage_loss: lineFields.percentage_loss.default(0),
    child_product_id: lineFields.child_product_id.optional(),
    notes: lineFields.notes.optional(),
    /** Invoegen ná deze regel (weglaten = onderaan) */
    after_line_id: z.number().int().positive().nullable().optional(),
  }),
  z.object({
    op: z.literal('update'),
    line_id: z.number().int().positive(),
    description: lineFields.description.optional(),
    unit4_product_code: lineFields.unit4_product_code.optional(),
    unit_code: lineFields.unit_code.optional(),
    qty_in_composition: lineFields.qty_in_composition.optional(),
    composition_per: lineFields.composition_per.optional(),
    percentage_loss: lineFields.percentage_loss.optional(),
    notes: lineFields.notes.optional(),
  }),
  z.object({ op: z.literal('remove'), line_id: z.number().int().positive() }),
]);
export type ChatLineOp = z.infer<typeof ChatLineOpSchema>;

export const ChatEditorPatchSchema = z.object({
  product_id: z.number().int().positive(),
  fields: ChatProductFieldsSchema.optional(),
  sales_description: ChatSalesDescriptionPatchSchema.optional(),
  line_ops: z.array(ChatLineOpSchema).optional(),
});
export type ChatEditorPatch = z.infer<typeof ChatEditorPatchSchema>;

/**
 * SSE-events van `POST /api/chat/conversations/:id/messages/stream` (één JSON-object per `data:`-regel;
 * de stream sluit af met het frame `event: end`).
 */
export type ChatStreamEvent =
  | { type: 'turn_start'; iteration: number }
  | {
      type: 'block_start';
      blockType: 'thinking' | 'text' | 'tool_use';
      toolName?: string;
      toolUseId?: string;
    }
  | { type: 'thinking_delta'; text: string }
  | { type: 'text_delta'; text: string }
  | { type: 'block_stop' }
  | { type: 'tool_call'; id: string; name: string; input: unknown; tier?: ChatToolTier }
  | { type: 'tool_result'; id: string; name: string; isError: boolean; output: string }
  /** Write/delete-tool wacht op bevestiging van de gebruiker (kaart met knoppen in het paneel). */
  | {
      type: 'confirmation_required';
      id: string;
      name: string;
      tier: ChatToolTier;
      input: unknown;
      summary: string;
      details: string | null;
    }
  | { type: 'confirmation_result'; id: string; decision: 'confirmed' | 'cancelled' | 'expired' }
  /** Concept-wijzigingen voor het geopende product (de frontend zet ze in de editor; gebruiker slaat op). */
  | { type: 'editor_patch'; patch: ChatEditorPatch }
  /** Een product is server-side gewijzigd (de frontend herlaadt het als het open staat). */
  | { type: 'product_changed'; product_id: number; action: string }
  | { type: 'warning'; message: string }
  | { type: 'error'; message: string; code?: string }
  | { type: 'done'; message: ChatMessage; conversation: ChatConversation };
